import { getQrImageSource } from 'utils/helpers/qrImage';
import React from 'react';
import { InputNumber, Loading } from 'components';
// import { useGetBankInfoQuery, useGetUserProfileQuery } from 'services';
import {
  useGetQrBidvVAQuery,
  useGetQrBidvVAWithAmountQuery,
  useGetUserProfileQuery,
} from 'services';
import { useRef, useState } from 'react';
import { useGetPointQRGalaxyPayQuery } from 'services/point/queries/useGetPointQRGalaxyPayQuery';
import QRCode from 'qrcode.react';
import { InputWraper } from 'modules';
import { Icon } from 'components/icon';

const MIN_DEPOSIT_AMOUNT = 10000;

const downloadQrImage = async (source: string, fileName: string) => {
  try {
    const response = await fetch(source);
    if (!response.ok) throw new Error('Unable to download QR image');

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(source, '_blank', 'noopener,noreferrer');
  }
};

const shareQrImage = async (
  source: string,
  fileName: string,
  title: string,
) => {
  if (!navigator.share) {
    await downloadQrImage(source, fileName);
    return;
  }

  try {
    const response = await fetch(source);
    if (!response.ok) throw new Error('Unable to share QR image');

    const blob = await response.blob();
    const file = new File([blob], fileName, {
      type: blob.type || 'image/png',
    });

    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ title, files: [file] });
      return;
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
  }

  if (source.startsWith('http')) {
    await navigator.share({ title, text: title, url: source });
  } else {
    await downloadQrImage(source, fileName);
  }
};

enum DepositType {
  TPBank = 'TPBank',
  BIDV = 'BIDV',
  GalaxyPay = 'GP',
}

const configTypeDeposit = [
  {
    label: 'Tiểu khoản BIDV',
    value: DepositType.BIDV,
    icon: '/assets/images/banks/bidv.png',
  },
  // {
  //   label: 'Tiểu khoản TPBank',
  //   value: DepositType.TPBank,
  //   icon: '/assets/images/banks/tpbank.png',
  // },
  // {
  //   label: 'Galaxy Pay',
  //   value: DepositType.GalaxyPay,
  // },
];

const TopUpPointsFeatures = (): JSX.Element => {
  const [typeDeposit, setTypeDeposit] = useState<DepositType | ''>(
    configTypeDeposit[0]?.value ?? '',
  );
  const [inputAmount, setInputAmount] = useState<number | null>(null);
  const [amountDeposit, setAmountDeposit] = useState<number | null>(0);
  const [qrRequestId, setQrRequestId] = useState(0);
  const bidvQrRef = useRef<HTMLDivElement>(null);
  const galaxyQrRef = useRef<HTMLDivElement>(null);

  const { data: dataDepositGP, isLoading: isLoadingDepositGP } =
    useGetPointQRGalaxyPayQuery({
      amount: amountDeposit?.toString() || '0',
      enabled: typeDeposit === DepositType.GalaxyPay,
      requestId: qrRequestId,
    });

  const { data, isLoading } = useGetUserProfileQuery();

  const { data: dataBidvQR, isFetching: isLoadingBidvQR } = useGetQrBidvVAQuery(
    {
      vaPrefix: data?.bidvVaPrefix || '',
      endCode: data?.bidvVaPayerId || '',
      enabled:
        typeDeposit === DepositType.BIDV &&
        !!data?.bidvVaPayerId &&
        !amountDeposit,
    },
  );

  const { data: dataBidvQRWithAmount, isFetching: isLoadingBidvQRWithAmount } =
    useGetQrBidvVAWithAmountQuery({
      vaPrefix: data?.bidvVaPrefix || '',
      endCode: data?.bidvVaPayerId || '',
      amount: amountDeposit,
      enabled: typeDeposit === DepositType.BIDV,
      requestId: qrRequestId,
    });

  if (isLoading || !data) {
    return <Loading backdrop />;
  }

  const handleGetQr = () => {
    const hasInvalidAmount =
      inputAmount !== null &&
      inputAmount > 0 &&
      inputAmount < MIN_DEPOSIT_AMOUNT;

    if (
      hasInvalidAmount ||
      (typeDeposit === DepositType.GalaxyPay &&
        (inputAmount === null || inputAmount < MIN_DEPOSIT_AMOUNT))
    ) {
      return;
    }

    setAmountDeposit(inputAmount || 0);
    setQrRequestId((currentRequestId) => currentRequestId + 1);
  };

  const handleKeyDown = (event: any) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      handleGetQr();
    }
  };

  const bidvQrImage = getQrImageSource(
    amountDeposit && amountDeposit > 0
      ? dataBidvQRWithAmount?.data?.vietQRImage
      : dataBidvQR?.data?.vietQRImage,
  );
  const tpBankQrImage = data.accountNumber
    ? `https://img.vietqr.io/image/tpbank-${data.accountNumber}-print.jpg`
    : '';
  const isAmountInvalid =
    inputAmount !== null && inputAmount > 0 && inputAmount < MIN_DEPOSIT_AMOUNT;

  return (
    <div className='card-wrapper container-fluid topup-points'>
      <h4 className='topup-points__title'>Phương thức nạp tiền</h4>
      <div className='topup-select'>
        {configTypeDeposit.map((deposit) => (
          <button
            key={deposit.value}
            type='button'
            className={`topup-select__tag ${
              typeDeposit === deposit.value ? 'active' : ''
            }`}
            aria-pressed={typeDeposit === deposit.value}
            onClick={() => {
              setTypeDeposit(deposit.value);
              setInputAmount(null);
              setAmountDeposit(0);
            }}
          >
            <span className='topup-select__tag-icon'>
              {deposit.icon ? (
                <img src={deposit.icon} alt='' aria-hidden='true' />
              ) : (
                <Icon.Wallet size={20} />
              )}
            </span>
            {deposit.label}
          </button>
        ))}
      </div>
      <div className='topup-points__warning' role='alert'>
        <Icon.Warning size={22} />
        <div>
          <strong>Lưu ý quan trọng</strong>
          <p>
            Vui lòng kiểm tra kỹ phương thức, ngân hàng, số tài khoản và tên
            người nhận trước khi chuyển khoản. Mọi giao dịch chuyển nhầm, hệ
            thống sẽ không chịu trách nhiệm.
          </p>
        </div>
      </div>
      {typeDeposit === DepositType.TPBank && (
        <div className='topup-points__content'>
          <h4 className='topup-points__subtitle'>
            Thông tin chuyển khoản TPBank
          </h4>
          <div className='topup-points__wrap'>
            {data.accountNumber ? (
              <>
                <img
                  id='inlineFrameExample'
                  // width="500"
                  height='500'
                  src={tpBankQrImage}
                ></img>
                <div className='topup-points__qr-actions'>
                  <button
                    type='button'
                    onClick={() =>
                      downloadQrImage(tpBankQrImage, 'QR_TPBank.png')
                    }
                  >
                    <Icon.Deposit size={16} />
                    Tải hình
                  </button>
                  <button
                    type='button'
                    onClick={() =>
                      shareQrImage(
                        tpBankQrImage,
                        'QR_TPBank.png',
                        'Mã QR nạp tiền TPBank',
                      )
                    }
                  >
                    <Icon.Affiliate size={16} />
                    Chia sẻ
                  </button>
                </div>
              </>
            ) : (
              <div className='text-center text-danger'>
                <h4>Không có thông tin Tiểu khoản TPBank tương ứng</h4>
              </div>
            )}
          </div>
        </div>
      )}
      {typeDeposit === DepositType.BIDV && (
        <div className='topup-points__content'>
          <h4 className='topup-points__subtitle'>
            Thông tin chuyển khoản BIDV
          </h4>
          <div className='topup-points__amount-row'>
            <InputWraper
              label='Nhập số tiền cần nạp'
              component={InputNumber}
              inputProps={{
                value: inputAmount ?? undefined,
                name: 'bidvAmount',
                onChange: (value) => {
                  setInputAmount(value);
                },
                onKeyDown: handleKeyDown,
                onTouched: () => {},
              }}
            />
            <button
              type='button'
              className='topup-points__qr-button'
              disabled={!data?.bidvVaPayerId || isAmountInvalid}
              onClick={handleGetQr}
            >
              Lấy mã QR
            </button>
          </div>
          {isAmountInvalid && (
            <p className='topup-points__amount-error'>
              Số tiền nạp phải lớn hơn 10.000 VNĐ.
            </p>
          )}
          <div className='topup-points__wrap'>
            {!data?.bidvVaPayerId ? (
              <div className='text-center text-danger'>
                <h4>Không có thông tin Tiểu khoản BIDV tương ứng</h4>
              </div>
            ) : (
              <Loading isLoading={isLoadingBidvQR || isLoadingBidvQRWithAmount}>
                {(dataBidvQRWithAmount || dataBidvQR) && (
                  <div
                    ref={bidvQrRef}
                    style={{
                      border: '1px solid blue',
                      background: 'white',
                      padding: '20px',
                      marginLeft: '20px',
                      maxWidth: '400px',
                    }}
                  >
                    {!bidvQrImage ? (
                      <div className='text-center text-danger'>
                        <h4>Không có mã QR BIDV</h4>
                      </div>
                    ) : (
                      <img
                        src={bidvQrImage}
                        alt='BIDV QR Code'
                        style={{
                          height: 'auto',
                          width: '100%',
                          maxWidth: '1024px',
                          padding: '20px',
                          border: '2px solid #32528B',
                          margin: 'auto',
                        }}
                      />
                    )}
                    {bidvQrImage && (
                      <div className='topup-points__qr-actions'>
                        <button
                          type='button'
                          onClick={() =>
                            downloadQrImage(bidvQrImage, 'QR_BIDV.png')
                          }
                        >
                          <Icon.Deposit size={16} />
                          Tải hình
                        </button>
                        <button
                          type='button'
                          onClick={() =>
                            shareQrImage(
                              bidvQrImage,
                              'QR_BIDV.png',
                              'Mã QR nạp tiền BIDV',
                            )
                          }
                        >
                          <Icon.Affiliate size={16} />
                          Chia sẻ
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!dataBidvQRWithAmount &&
                  !dataBidvQR &&
                  !isLoadingBidvQR &&
                  !isLoadingBidvQRWithAmount && (
                    <div className='text-center text-danger'>
                      <h4>Không thể tạo QR BIDV</h4>
                    </div>
                  )}
              </Loading>
            )}
          </div>
        </div>
      )}
      {typeDeposit === DepositType.GalaxyPay && (
        <div className='topup-points__content'>
          <h4 className='topup-points__subtitle'>Thông tin chuyển khoản</h4>
          <div className='topup-points__amount-row'>
            <InputWraper
              label='Nhập số tiền cần nạp'
              component={InputNumber}
              inputProps={{
                value: inputAmount ?? undefined,
                name: 'galaxyPayAmount',
                onChange: (value) => {
                  setInputAmount(value);
                },
                onKeyDown: handleKeyDown,
                onTouched: () => {},
              }}
            />
            <button
              type='button'
              className='topup-points__qr-button'
              disabled={!inputAmount || inputAmount <= MIN_DEPOSIT_AMOUNT}
              onClick={handleGetQr}
            >
              Lấy mã QR
            </button>
          </div>
          {isAmountInvalid && (
            <p className='topup-points__amount-error'>
              Số tiền nạp phải lớn hơn 10.000 VNĐ.
            </p>
          )}
          {amountDeposit !== null && amountDeposit > 0 && (
            <>
              <Loading isLoading={isLoadingDepositGP}>
                {dataDepositGP && (
                  <div
                    ref={galaxyQrRef}
                    style={{
                      border: '1px solid blue',
                      background: 'white',
                      padding: '20px',
                      marginLeft: '20px',
                      maxWidth: '400px',
                    }}
                  >
                    <QRCode
                      size={256}
                      style={{
                        height: 'auto',
                        width: '100%',
                        padding: '20px',
                        border: '2px solid #32528B',
                        margin: 'auto',
                      }}
                      value={dataDepositGP?.data?.qrInfo ?? ''}
                      imageSettings={{
                        src: '/assets/images/logo.png',
                        width: 40,
                        height: 40,
                        excavate: true,
                      }}
                    />
                    <h4 className='topup-points__text'>
                      {dataDepositGP?.data?.bankName ?? ''}
                    </h4>
                    <h4 className='topup-points__text'>
                      Số TK: {dataDepositGP?.data?.accountNo ?? ''}
                    </h4>
                    <h4 className='topup-points__text'>
                      Tên TK: {dataDepositGP?.data?.accountName ?? ''}
                    </h4>
                    <div className='topup-points__qr-actions'>
                      <button
                        type='button'
                        onClick={() => {
                          const canvas =
                            galaxyQrRef.current?.querySelector('canvas');
                          const qrImage = canvas?.toDataURL('image/png');
                          if (qrImage)
                            downloadQrImage(qrImage, 'QR_GalaxyPay.png');
                        }}
                      >
                        <Icon.Deposit size={16} />
                        Tải hình
                      </button>
                      <button
                        type='button'
                        onClick={() => {
                          const canvas =
                            galaxyQrRef.current?.querySelector('canvas');
                          const qrImage = canvas?.toDataURL('image/png');
                          if (qrImage)
                            shareQrImage(
                              qrImage,
                              'QR_GalaxyPay.png',
                              'Mã QR nạp tiền Galaxy Pay',
                            );
                        }}
                      >
                        <Icon.Affiliate size={16} />
                        Chia sẻ
                      </button>
                    </div>
                  </div>
                )}
              </Loading>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TopUpPointsFeatures;
